import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { Badge, Button, Drawer, Layout, List, Space, Tabs, Tag, Tooltip, message } from "antd";
import {
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import Logo from "./Logo";
import HeaderBreadcrumb from "./HeaderBreadcrumb";
import { onMobileNavToggle, toggleCollapsedNav } from "../../redux/actions/Theme";
import { NAV_TYPE_TOP, SIDE_NAV_COLLAPSED_WIDTH, SIDE_NAV_WIDTH } from "constants/ThemeConstant";
import utils from "utils";
import { useFirebase, useFirestore } from "react-redux-firebase";
import { useTranslation } from "react-i18next";
import * as dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY_HH_mm } from "../../constants/DateConstant";
import { useCrmTasks } from "../../data/hooks/useCrmTasks";
import { usePurchaseOrderNotifications } from "../../data/hooks/usePurchaseOrderNotifications";
import { useProductNotifications } from "../../data/hooks/useProductNotifications";
import urls from "../../urls";
import CustomerDrawer from "../shared-components/Customers/CustomerDrawer";
import OrderDrawer from "../shared-components/Orders/OrderDrawer";
import { FIRESTORE_USERS_TABLE } from "../../constants/FirestoreConstant";
import CrmContactMomentModal from "../shared-components/Customers/CrmContactMomentModal";
import { usePermissions } from "../../data/hooks/usePermissions";
import { useScopedOrders } from "../../data/hooks/useScopedOrders";
import { useScopedCustomers } from "../../data/hooks/useScopedCustomers";
import { PERMISSIONS } from "../../functions/permissions";
import { ORDER_STATUS_CREATED } from "../../constants/OrderConstant";
import { formatPrice } from "../../functions/formatters";
import { CURRENCY } from "../../constants/DeliveryConstants";

const { Header } = Layout;

export const HeaderNav = (props) => {
  const {
    navCollapsed,
    mobileNav,
    navType,
    headerNavColor,
    toggleCollapsedNav,
    // onMobileNavToggle,
    isMobile,
    currentTheme,
  } = props;
  const firebase = useFirebase();
  const firestore = useFirestore();
  const { t } = useTranslation();
  const authEmail = useSelector((state) => state.firebaseReducer.profile.email);
  const auth = useSelector((state) => state.firebaseReducer.auth);
  // Beperkt tot de landen van de gebruiker, net als de klantenlijst.
  const allUsers = useScopedCustomers();
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();

  const [tasksDrawerOpen, setTasksDrawerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDrawerVisible, setCustomerDrawerVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  const [contactMomentModalVisible, setContactMomentModalVisible] = useState(false);
  const [contactMomentUserId, setContactMomentUserId] = useState(null);
  const [contactMomentCustomerName, setContactMomentCustomerName] = useState("");
  const [contactMomentText, setContactMomentText] = useState("");
  const [contactMomentDateTime, setContactMomentDateTime] = useState(null);
  const [contactMomentFileList, setContactMomentFileList] = useState([]);
  const [savingContactMoment, setSavingContactMoment] = useState(false);

  // Use the custom hook to fetch CRM tasks assigned to the current user
  const { tasks: assignedTasks } = useCrmTasks({
    assignedToUid: auth?.uid || null,
    autoFetch: !!auth?.uid,
    realtime: true,
  });

  const navigate = useNavigate();

  const { can } = usePermissions();
  const canViewOrders = can(PERMISSIONS.ORDERS_VIEW);
  const canManagePurchasing = can(PERMISSIONS.PURCHASING_MANAGE);
  const canManageProducts = can(PERMISSIONS.PRODUCTS_MANAGE);

  // Inkoopmeldingen alleen ophalen voor wie inkoop mag zien.
  const {
    notifications: purchaseNotifications,
    markResolved,
    markRead,
  } = usePurchaseOrderNotifications({
    onlyUnresolved: true,
    enabled: canManagePurchasing,
  });

  // Productmeldingen (BMX-49): geschreven door een Cloud Function, hier alleen gelezen.
  const {
    notifications: productNotifications,
    markResolved: markProductResolved,
    markRead: markProductRead,
  } = useProductNotifications({ onlyUnresolved: true, enabled: canManageProducts });

  // Nieuwe orders in de eigen regio (BMX-35). De orders komen uit dezelfde
  // store als de orderlijst, al beperkt tot de landen van de gebruiker. "Gezien"
  // staat per gebruiker in localStorage; bij het eerste gebruik zetten we het
  // op nu, zodat niemand een badge met honderden oude orders krijgt.
  const scopedOrders = useScopedOrders();
  const ordersSeenKey = auth?.uid ? `cms.orderNotificationsSeenAt.${auth.uid}` : null;
  const [ordersSeenAt, setOrdersSeenAt] = useState(null);
  useEffect(() => {
    if (!ordersSeenKey) return;
    let stored = null;
    try {
      stored = Number(window.localStorage.getItem(ordersSeenKey)) || null;
    } catch (e) {
      stored = null;
    }
    if (!stored) {
      stored = dayjs().unix();
      try {
        window.localStorage.setItem(ordersSeenKey, String(stored));
      } catch (e) {
        // localStorage niet beschikbaar: dan telt alles als gezien
      }
    }
    setOrdersSeenAt(stored);
  }, [ordersSeenKey]);

  const markOrdersSeen = () => {
    const now = dayjs().unix();
    setOrdersSeenAt(now);
    if (!ordersSeenKey) return;
    try {
      window.localStorage.setItem(ordersSeenKey, String(now));
    } catch (e) {
      // zie boven
    }
  };

  const recentOrders = useMemo(() => {
    if (!canViewOrders) return [];
    return (scopedOrders || [])
      .filter((order) => (order?.status?.current ?? order?.orderStatus) !== ORDER_STATUS_CREATED)
      .sort((a, b) => (b?.createdAt || 0) - (a?.createdAt || 0))
      .slice(0, 25);
  }, [scopedOrders, canViewOrders]);

  const unseenOrders = useMemo(
    () => (ordersSeenAt ? recentOrders.filter((order) => (order?.createdAt || 0) > ordersSeenAt) : []),
    [recentOrders, ordersSeenAt],
  );

  const dueSoonTasks = useMemo(() => {
    const now = dayjs().unix();
    const soon = dayjs().add(24, "hour").unix();
    return (assignedTasks || [])
      .filter((t) => (t?.status || "open") === "open")
      .filter((t) => !t?.dueAt || (typeof t.dueAt === "number" && t.dueAt <= soon))
      .sort((a, b) => (a?.dueAt || 0) - (b?.dueAt || 0))
      .map((t) => ({
        ...t,
        _isOverdue: typeof t?.dueAt === "number" ? t.dueAt < now : false,
      }));
  }, [assignedTasks]);

  const unreadPurchaseNotifications = useMemo(() => {
    return (purchaseNotifications || []).filter((n) => !n.readBy?.includes(auth?.uid));
  }, [purchaseNotifications, auth?.uid]);

  // Ongelezen productmeldingen. "Gelezen" staat per gebruiker in het document zelf (`readBy`),
  // dus het klopt ook op een tweede apparaat -- anders dan de localStorage van het Orders-tabblad.
  const unreadProductNotifications = useMemo(
    () => (productNotifications || []).filter((n) => !n.readBy?.includes(auth?.uid)),
    [productNotifications, auth?.uid],
  );

  const badgeCount =
    dueSoonTasks.length +
    unreadPurchaseNotifications.length +
    unseenOrders.length +
    unreadProductNotifications.length;

  const getSeverityIcon = (severity) => {
    if (severity === "critical") return <ExclamationCircleOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />;
    if (severity === "warning") return <WarningOutlined style={{ color: "#fa8c16", fontSize: 16 }} />;
    return <InfoCircleOutlined style={{ color: "#1890ff", fontSize: 16 }} />;
  };

  const getSeverityColor = (severity) => {
    if (severity === "critical") return "red";
    if (severity === "warning") return "orange";
    return "blue";
  };

  const onToggle = () => {
    if (!isMobile) {
      toggleCollapsedNav(!navCollapsed);
    } else {
      dispatch(onMobileNavToggle(!mobileNav));
    }
  };

  const isNavTop = navType === NAV_TYPE_TOP ? true : false;
  const mode = () => {
    if (!headerNavColor) {
      return utils.getColorContrast(currentTheme === "dark" ? "#00000" : "#ffffff");
    }
    return utils.getColorContrast(headerNavColor);
  };
  const navMode = mode();
  const getNavWidth = () => {
    if (isNavTop || isMobile) {
      return "0px";
    }
    if (navCollapsed) {
      return `${SIDE_NAV_COLLAPSED_WIDTH}px`;
    } else {
      return `${SIDE_NAV_WIDTH}px`;
    }
  };

  const handleSignOut = (e) => {
    e.preventDefault();
    firebase.logout().catch((err) => console.log(err));
  };

  const resetContactMomentState = () => {
    setContactMomentModalVisible(false);
    setContactMomentUserId(null);
    setContactMomentCustomerName("");
    setContactMomentText("");
    setContactMomentDateTime(null);
    setContactMomentFileList([]);
    setSavingContactMoment(false);
  };

  const openContactMomentModalForTask = (task) => {
    if (!task?.customerUserId) return;
    const customerName =
      task?.customerCompanyName || allUsers?.find((u) => u.id === task.customerUserId)?.company?.name || "";
    setContactMomentUserId(task.customerUserId);
    setContactMomentCustomerName(customerName);
    setContactMomentDateTime(new Date());
    setContactMomentText((prev) => {
      const trimmed = (prev || "").trim();
      if (trimmed) return prev;
      const taskTitle = (task?.title || "").trim();
      return taskTitle ? taskTitle : "";
    });
    setContactMomentFileList([]);
    setContactMomentModalVisible(true);
  };

  const saveContactMoment = async () => {
    if (!contactMomentUserId) return;
    if (!contactMomentText.trim() && contactMomentFileList.length === 0) {
      messageApi.warning(t("please_enter_note_or_attachment"));
      return;
    }

    try {
      setSavingContactMoment(true);
      const noteRef = firestore.collection(FIRESTORE_USERS_TABLE).doc(contactMomentUserId).collection("crmNotes").doc();

      const attachments = [];
      for (const file of contactMomentFileList) {
        if (file.originFileObj) {
          const storageRef = firebase.storage().ref(`users/${contactMomentUserId}/crmNotes/${noteRef.id}/${file.name}`);
          await storageRef.put(file.originFileObj);
          const url = await storageRef.getDownloadURL();
          attachments.push({ name: file.name, url, path: storageRef.fullPath });
        }
      }

      const now = dayjs().unix();
      const dateTimeUnix = contactMomentDateTime ? dayjs(contactMomentDateTime).unix() : now;

      await noteRef.set({
        text: contactMomentText.trim(),
        dateTime: dateTimeUnix,
        createdAt: now,
        createdBy: authEmail,
        createdByUid: auth.uid,
        attachments,
      });

      messageApi.success(t("saved"));
      resetContactMomentState();
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    } finally {
      setSavingContactMoment(false);
    }
  };

  const markTaskDone = async (task) => {
    if (!task?.customerUserId || !task?.id) return;
    try {
      await firestore
        .collection(FIRESTORE_USERS_TABLE)
        .doc(task.customerUserId)
        .collection("crmTasks")
        .doc(task.id)
        .update({
          status: "done",
          completedAt: Math.floor(Date.now() / 1000),
          completedBy: auth.email,
        });
      messageApi.success(t("task_completed"));

      openContactMomentModalForTask(task);
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    }
  };

  const openCustomerDrawer = (task) => {
    if (!task?.customerUserId) return;
    const customer = allUsers?.find((u) => u.id === task.customerUserId);
    if (customer) {
      setSelectedCustomer(customer);
      setCustomerDrawerVisible(true);
      setTasksDrawerOpen(false);
    }
  };

  return (
    <>
      {contextHolder}
      <Header className={`app-header ${navMode}`} style={{ backgroundColor: "#FFF" }}>
        <div className={`app-header-wrapper ${isNavTop ? "layout-top-nav" : ""}`}>
          <Logo logoType={navMode} />
          <div className="nav" style={{ width: `calc(100% - ${getNavWidth()})` }}>
            {/* minWidth/overflow zijn nodig: zonder die twee duwt een lang
                recordnummer de bel-knop uit beeld in plaats van zelf af te kappen.
                .nav-left is in globals.css alleen `display:flex`. */}
            <div className="nav-left" style={{ alignItems: "center", gap: 12, minWidth: 0, overflow: "hidden" }}>
              {isNavTop && !isMobile ? null : navCollapsed || isMobile ? (
                <MenuUnfoldOutlined
                  className="nav-icon"
                  onClick={() => {
                    onToggle();
                  }}
                />
              ) : (
                <MenuFoldOutlined
                  className="nav-icon"
                  onClick={() => {
                    onToggle();
                  }}
                />
              )}
              {/* Op mobiel is er geen ruimte in de balk. */}
              {!isMobile && <HeaderBreadcrumb />}
            </div>
            <div style={{ display: "flex", alignItems: "center", flex: "none" }}>
              <Button
                type="text"
                onClick={() => setTasksDrawerOpen(true)}
                icon={
                  <Badge count={badgeCount} size="small" overflowCount={99}>
                    <BellOutlined style={{ fontSize: 18 }} />
                  </Badge>
                }
              />
            </div>
          </div>
        </div>
        <Drawer
          title="Notificaties"
          placement="right"
          open={tasksDrawerOpen}
          onClose={() => setTasksDrawerOpen(false)}
          width={460}
          bodyStyle={{ padding: 0 }}
        >
          <Tabs
            defaultActiveKey="taken"
            style={{ padding: "0 16px" }}
            onChange={(key) => {
              if (key === "orders") markOrdersSeen();
            }}
            items={[
              {
                key: "taken",
                label: (
                  <Badge count={dueSoonTasks.length} size="small" offset={[6, -2]}>
                    <span style={{ paddingRight: 8 }}>{t("tasks")}</span>
                  </Badge>
                ),
                children: (
                  <List
                    dataSource={dueSoonTasks}
                    locale={{ emptyText: t("no_data") }}
                    renderItem={(task) => {
                      const dueAt = typeof task?.dueAt === "number" ? dayjs.unix(task.dueAt) : null;
                      return (
                        <List.Item
                          key={task.id}
                          actions={[
                            <Button
                              key="done"
                              type="text"
                              icon={<CheckOutlined />}
                              onClick={() => markTaskDone(task)}
                              title={t("finish")}
                            />,
                          ]}
                        >
                          <List.Item.Meta
                            title={
                              <div
                                style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}
                              >
                                <a
                                  onClick={() => openCustomerDrawer(task)}
                                  style={{ fontWeight: 600, cursor: "pointer" }}
                                >
                                  {task.title || "-"}
                                </a>
                                <span style={{ color: task._isOverdue ? "#ff4d4f" : "#999", whiteSpace: "nowrap" }}>
                                  {dueAt ? dueAt.format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
                                </span>
                              </div>
                            }
                            description={
                              <div style={{ color: "#666" }}>
                                {task.customerCompanyName ? task.customerCompanyName : task.customerUserId || ""}
                              </div>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                ),
              },
              canViewOrders && {
                key: "orders",
                label: (
                  <Badge count={unseenOrders.length} size="small" offset={[6, -2]}>
                    <span style={{ paddingRight: 8 }}>{t("order_notifications.tab")}</span>
                  </Badge>
                ),
                children: (
                  <List
                    dataSource={recentOrders}
                    locale={{ emptyText: t("order_notifications.empty") }}
                    renderItem={(order) => {
                      const createdAt = order?.createdAt ? dayjs.unix(order.createdAt) : null;
                      const isUnseen = !!ordersSeenAt && (order?.createdAt || 0) > ordersSeenAt;
                      return (
                        <List.Item
                          key={order.id}
                          style={{
                            background: isUnseen ? "#fffbe6" : "transparent",
                            borderLeft: `3px solid ${isUnseen ? "#faad14" : "transparent"}`,
                            paddingLeft: 12,
                            marginBottom: 4,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setTasksDrawerOpen(false);
                            setCurrentOrder(order);
                          }}
                        >
                          <List.Item.Meta
                            title={
                              <div
                                style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}
                              >
                                <span style={{ fontWeight: isUnseen ? 700 : 500, fontSize: 13 }}>
                                  {order.orderId || "-"}
                                </span>
                                {order?.customer?.country && (
                                  <Tag style={{ fontSize: 11 }}>{order.customer.country}</Tag>
                                )}
                              </div>
                            }
                            description={
                              <div>
                                <div style={{ color: "#333", fontSize: 13 }}>
                                  {order?.customer?.company ||
                                    `${order?.customer?.firstName || ""} ${order?.customer?.lastName || ""}`.trim() ||
                                    "-"}
                                </div>
                                <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                                  {createdAt ? createdAt.format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
                                  {typeof order?.totalPrice === "number"
                                    ? ` · ${CURRENCY}${formatPrice(order.totalPrice)}`
                                    : ""}
                                </div>
                              </div>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                ),
              },
              canManagePurchasing && {
                key: "inkoop",
                label: (
                  <Badge count={unreadPurchaseNotifications.length} size="small" offset={[6, -2]}>
                    <span style={{ paddingRight: 8 }}>Inkoop</span>
                  </Badge>
                ),
                children: (
                  <List
                    dataSource={purchaseNotifications || []}
                    locale={{ emptyText: "Geen openstaande meldingen" }}
                    renderItem={(notification) => {
                      const createdAt = notification.createdAt ? dayjs.unix(notification.createdAt) : null;
                      const isUnread = !notification.readBy?.includes(auth?.uid);
                      return (
                        <List.Item
                          key={notification.id}
                          style={{
                            background: isUnread ? "#fffbe6" : "transparent",
                            borderLeft: `3px solid ${notification.severity === "critical" ? "#ff4d4f" : notification.severity === "warning" ? "#fa8c16" : "#1890ff"}`,
                            paddingLeft: 12,
                            marginBottom: 4,
                            cursor: notification.orderId ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (!notification.orderId) return;
                            if (isUnread && auth?.uid) markRead(notification.id, auth.uid);
                            setTasksDrawerOpen(false);
                            navigate(`/cms/orders/purchase-orders/${notification.orderId}`);
                          }}
                          actions={[
                            <Tooltip key="resolve" title="Afgehandeld">
                              <Button
                                type="text"
                                size="small"
                                icon={<CheckCircleOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markResolved(notification.id, authEmail);
                                }}
                              />
                            </Tooltip>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={getSeverityIcon(notification.severity)}
                            title={
                              <div
                                style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}
                              >
                                <span style={{ fontWeight: isUnread ? 700 : 500, fontSize: 13 }}>
                                  {notification.orderNumber || "-"}
                                </span>
                                <Tag color={getSeverityColor(notification.severity)} style={{ fontSize: 11 }}>
                                  {notification.severity === "critical"
                                    ? "Kritiek"
                                    : notification.severity === "warning"
                                      ? "Waarschuwing"
                                      : "Info"}
                                </Tag>
                              </div>
                            }
                            description={
                              <div>
                                <div style={{ color: "#333", fontSize: 13, marginBottom: 4 }}>
                                  {notification.message || "-"}
                                </div>
                                {notification.products && notification.products.length > 0 && (
                                  <div style={{ fontSize: 12, color: "#666" }}>
                                    {notification.products.map((p, i) => (
                                      <div key={i}>
                                        <strong>{p.productCode}</strong> {p.productName}: {p.changeDescription}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                                  {createdAt ? createdAt.format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
                                </div>
                              </div>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                ),
              },
              canManageProducts && {
                key: "producten",
                label: (
                  <Badge count={unreadProductNotifications.length} size="small" offset={[6, -2]}>
                    <span style={{ paddingRight: 8 }}>{t("products")}</span>
                  </Badge>
                ),
                children: (
                  <List
                    dataSource={productNotifications || []}
                    locale={{ emptyText: "Geen openstaande meldingen" }}
                    renderItem={(notification) => {
                      const createdAt = notification.createdAt ? dayjs.unix(notification.createdAt) : null;
                      const isUnread = !notification.readBy?.includes(auth?.uid);
                      return (
                        <List.Item
                          key={notification.id}
                          style={{
                            background: isUnread ? "#fffbe6" : "transparent",
                            borderLeft: "3px solid #fa8c16",
                            paddingLeft: 12,
                            marginBottom: 4,
                            cursor: notification.productId ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (!notification.productId) return;
                            if (isUnread && auth?.uid) markProductRead(notification.id, auth.uid);
                            setTasksDrawerOpen(false);
                            navigate(urls.editProduct + notification.productId);
                          }}
                          actions={[
                            <Tooltip key="resolve" title="Afgehandeld">
                              <Button
                                type="text"
                                size="small"
                                icon={<CheckCircleOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markProductResolved(notification.id, authEmail);
                                }}
                              />
                            </Tooltip>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={<WarningOutlined style={{ color: "#fa8c16", fontSize: 16 }} />}
                            title={
                              <div
                                style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}
                              >
                                <span style={{ fontWeight: isUnread ? 700 : 500, fontSize: 13 }}>
                                  {notification.productName || "-"}
                                </span>
                                <Tag color="orange" style={{ fontSize: 11 }}>
                                  Uitlopend
                                </Tag>
                              </div>
                            }
                            description={
                              <div style={{ fontSize: 13, color: "#333" }}>
                                Nog {notification.stock} op voorraad, onder de bestelgrens van{" "}
                                {notification.threshold}.
                                <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>
                                  {notification.productCode ? `${notification.productCode} · ` : ""}
                                  {createdAt ? createdAt.format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
                                </div>
                              </div>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                ),
              },
            ].filter(Boolean)}
          />
        </Drawer>

        <CrmContactMomentModal
          open={contactMomentModalVisible}
          onCancel={resetContactMomentState}
          onOk={saveContactMoment}
          confirmLoading={savingContactMoment}
          customerName={contactMomentCustomerName}
          dateTime={contactMomentDateTime}
          onDateTimeChange={setContactMomentDateTime}
          text={contactMomentText}
          onTextChange={setContactMomentText}
          fileList={contactMomentFileList}
          onFileListChange={setContactMomentFileList}
        />

        <CustomerDrawer
          user={selectedCustomer}
          visible={customerDrawerVisible}
          onClose={() => {
            setCustomerDrawerVisible(false);
            setSelectedCustomer(null);
          }}
          onOrderClick={(order) => setCurrentOrder(order)}
        />
        <OrderDrawer order={currentOrder} open={!!currentOrder} closeDrawer={() => setCurrentOrder(null)} />
      </Header>
    </>
  );
};

const mapStateToProps = ({ theme }) => {
  const { navCollapsed, navType, headerNavColor, mobileNav, currentTheme } = theme;
  return {
    navCollapsed,
    navType,
    headerNavColor,
    mobileNav,
    currentTheme,
  };
};

export default connect(mapStateToProps, { toggleCollapsedNav, onMobileNavToggle })(HeaderNav);
